import "./../views/_menupage.css";
import { JitsiMeeting } from "@jitsi/react-sdk";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import VideoMosaic from "./VideoMosaic";

import { useBrand } from "./../brand";
import { fromJitsiLang, getLang, jitsiLang, useT, useLang } from "./../i18n";
import { CloseIcon, MuteAllIcon, PencilIcon, RemoveUserIcon } from "./icons";
import { startTouchDebug } from "./../touch-debug";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
// 預設同源的 /wbo/boards —— 內網部署一定是 nginx 把白板掛在同一個來源之下。
// 沒有預設值的話，缺 .env 時會變成字串 "undefined/房名"，白板直接開不起來。
const WHITEBOARD_BASE_URL =
  process.env.REACT_APP_WHITEBOARD_BASE_URL || "/wbo/boards";
// Jitsi 位址一律在執行時決定，不在編譯時寫死 —— 否則換機器、換埠都要重新 build。
// 順序：.env 指定 > 後端 /api/config > 目前網址的主機名 + 8443。
const BUILD_TIME_DOMAIN = process.env.REACT_APP_JITSI_DOMAIN || null;

// 預設的 Jitsi 掛載路徑。與 nginx 的 location /jitsi/ 和
// deploy/jitsi/.env 的 PUBLIC_URL 一致。
const DEFAULT_JITSI_PATH = "/jitsi";

const sameOrigin = (path) =>
  `${window.location.host}${path}`.replace(/\/+$/, "");

const resolveJitsiDomain = (cfg) => {
  if (BUILD_TIME_DOMAIN) return BUILD_TIME_DOMAIN;
  // 同源子路徑優先。用 location.host（含埠）＋ 路徑，
  // 這樣不論使用者是用 IP 還是主機名進來都對得上，也不必重新編譯。
  if (cfg && cfg.jitsiPath) return sameOrigin(cfg.jitsiPath);
  // 只有後端明確指定「Jitsi 在別台」時才走獨立來源
  if (cfg && cfg.jitsiDomain) return cfg.jitsiDomain;
  if (cfg && cfg.jitsiPort) return `${window.location.hostname}:${cfg.jitsiPort}`;
  // 拿不到設定時的退路。這裡一定要用同源路徑：
  // Jitsi 容器只綁 127.0.0.1，對外沒有 8443，退回那個埠必定是一片全黑。
  return sameOrigin(DEFAULT_JITSI_PATH);
};

// 自架 Jitsi 若尚未開啟 ENABLE_AUTH=1 / AUTH_TYPE=jwt，prosody 只接受 ANONYMOUS，
// 這時候送 JWT 反而會被拒絕，所以做成開關。
// 預設「開啟」，要關才設成 false。
//
// 原本寫 === "true"，也就是沒有 .env 就等於關掉。但 update.ps1 產生的
// jitsi\.env 一律是 ENABLE_AUTH=1 + AUTH_TYPE=jwt —— 伺服器永遠要求 JWT。
// 前端不送的話，Jitsi 會跳出「需要驗證」要使用者輸入帳號密碼。
// 而 .env 不進版控，換一台機器 build 就會無聲無息關掉，症狀還完全看不出
// 是前端造成的（伺服器端只會說 "token required"）。
// 所以改成 opt-out：預設跟伺服器一致，要關再明講。
const JWT_ENABLED = process.env.REACT_APP_JITSI_JWT_ENABLED !== "false";

// 電腦端的影像壓縮格式偏好。Jitsi 預設 AV1 → VP9 → VP8 → H264，
// AV1/VP9 在 Chrome 是純 CPU 軟體壓縮，最吃 CPU；開刀房主機同時跑 AI
// 或影像處理時會搶 CPU，畫面就卡。H264 多半能交給顯示卡的硬體壓縮器，
// 內網頻寬充足，AV1 省頻寬的優勢用不到。手機端維持 Jitsi 預設（VP8 優先）。
const DESKTOP_CODEC_ORDER = ["H264", "VP8", "VP9", "AV1"];

const MeetingRoom = () => {
  const { id } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  // Jitsi 內部一律把房名正規化成小寫，白板網址與 JWT 都要跟著小寫，否則對不上。
  const roomName = (id || "").toLowerCase();

  const companyId = params.get("company") || "";
  const role = params.get("role") || "guest";
  const isHost = role === "host";
  const isTouchDevice = Boolean(window.matchMedia?.("(pointer: coarse)").matches);
  // 網址加 ?debug=1：手機每次碰螢幕都送記錄到伺服器，電腦開 /api/debug/touch/view 看
  const debugTouch = params.get("debug") === "1";
  const t = useT();
  const [lang, setLang] = useLang();
  // 固定在進會議那一刻：預設名稱會跟著語言變，而它是取 JWT 的條件之一，
  // 會議中途換語言不該重新要一次憑證
  const [displayName] = useState(
    () => params.get("name") || (isHost ? t("meeting.host") : t("meeting.guest"))
  );

  const brand = useBrand();
  const navigate = useNavigate();
  const apiRef = useRef(null);
  const meetingFrameRef = useRef(null);
  const whiteboardFrameRef = useRef(null);
  const participantsRef = useRef([]);
  const myIdRef = useRef("");
  const joinedAtRef = useRef(0);

  const [jwtToken, setJwtToken] = useState(null);
  const [tokenReady, setTokenReady] = useState(!JWT_ENABLED);
  const [jitsiDomain, setJitsiDomain] = useState(BUILD_TIME_DOMAIN);
  const [toggleWBO, setToggleWBO] = useState(false);
  // 直向提醒按過「仍要直向使用」就不再出現（這次開會期間）
  const [rotateDismissed, setRotateDismissed] = useState(false);
  const [participants, setParticipants] = useState([]);
  // 主持人的「移除參與者」名單：平常收起，點圖示才展開
  const [kickOpen, setKickOpen] = useState(false);
  const kickRef = useRef(null);
  // 白板框選之馬賽克打碼區域（與底層視訊即時聯動高斯模糊）
  const [mosaics, setMosaics] = useState([]);

  useEffect(() => {
    if (!toggleWBO) setMosaics([]);
  }, [toggleWBO]);

  // 使用者在 Jitsi 設定（齒輪 → 一般）換語言時，我們自己的按鈕、名單、確認視窗也跟著換。
  // Jitsi 沒有「語言改了」的事件，所以看兩個地方（同源才讀得到）：
  //   - localStorage 的 "language"：Jitsi 換語言時會記在這裡（跟我們同源，共用同一份）
  //   - Jitsi iframe 的 <html lang>
  // 只理會進會議 5 秒之後的「變化」：Jitsi 剛載入時這兩個值也會從空的變成初始值，
  // 那不是使用者換語言，不能拿來蓋掉使用者在首頁選的語言。
  useEffect(() => {
    const read = () => {
      let stored = null;
      let html = null;
      try { stored = window.localStorage.getItem("language"); } catch (e) {}
      try {
        html = meetingFrameRef.current?.querySelector("iframe")?.contentDocument?.documentElement?.lang || null;
      } catch (e) {}
      return { stored, html };
    };
    // "zh-TW"／"zhTW"、"zh-CN"／"zhCN"／"zh" 這些寫法都可能出現，先統一成 Jitsi 的代碼
    const normalize = (code) => {
      const c = String(code).replace(/[-_]/g, "").toLowerCase();
      if (c === "zhtw" || c === "zhhant") return "zhTW";
      if (c === "zhcn" || c === "zhhans" || c === "zh") return "zhCN";
      return c.slice(0, 2) === "ja" ? "ja" : c;
    };
    let last = read();
    const check = () => {
      const now = read();
      if (!joinedAtRef.current || Date.now() - joinedAtRef.current < 5000) {
        last = now; // 還在載入期，只更新基準
        return;
      }
      const changed = (now.stored && now.stored !== last.stored && now.stored) ||
        (now.html && now.html !== last.html && now.html);
      last = now;
      if (!changed) return;
      const next = fromJitsiLang(normalize(changed));
      if (next !== getLang()) setLang(next);
    };
    window.addEventListener("storage", check);
    const timer = setInterval(check, 1500);
    return () => {
      window.removeEventListener("storage", check);
      clearInterval(timer);
    };
  }, [setLang]);

  // 移除參與者的名單開著時，點名單以外的地方就收起來
  useEffect(() => {
    if (!kickOpen) return;
    const close = (e) => {
      if (kickRef.current && !kickRef.current.contains(e.target)) setKickOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [kickOpen]);

  // 會議頁期間鎖住整頁：不能捲動、不能「點兩下放大」（樣式見 App.css 的 html.in-meeting）。
  // 手機上頁面一旦被放大或拉動，固定位置的按鈕跟實際點擊位置會錯開。
  useEffect(() => {
    document.documentElement.classList.add("in-meeting");
    // 保險：萬一 iPhone 還是把整頁捲動了（網址列收起、轉向），立刻捲回原點，
    // 否則畫面位置和點擊位置會一直錯開。
    const resetScroll = () => {
      if (window.scrollX || window.scrollY) window.scrollTo(0, 0);
    };
    const vv = window.visualViewport;
    window.addEventListener("scroll", resetScroll, { passive: true });
    window.addEventListener("orientationchange", resetScroll);
    vv && vv.addEventListener("resize", resetScroll);
    return () => {
      document.documentElement.classList.remove("in-meeting");
      window.removeEventListener("scroll", resetScroll);
      window.removeEventListener("orientationchange", resetScroll);
      vv && vv.removeEventListener("resize", resetScroll);
    };
  }, []);

  useEffect(() => {
    if (!debugTouch) return;
    return startTouchDebug(() => [
      ["jitsi", meetingFrameRef.current?.querySelector("iframe")],
      ["wbo", whiteboardFrameRef.current],
    ]);
  }, [debugTouch]);

  // 白板開著時把 Jitsi 底部工具列收起來。白板 iframe 蓋在 Jitsi 上面，
  // 那排按鈕本來就點不到，留著只會擋住畫面；關白板就恢復。
  // Jitsi 沒有「隱藏工具列」的 API，所以直接在它的 iframe 裡加一段樣式
  // （同源才做得到；Jitsi 若在別的來源，這裡會靜默略過，不影響其他功能）。
  useEffect(() => {
    const STYLE_ID = "ormaster-hide-toolbox";
    let doc = null;
    try {
      doc = meetingFrameRef.current?.querySelector("iframe")?.contentDocument;
    } catch (e) {}
    if (!doc?.head) return;
    doc.getElementById(STYLE_ID)?.remove();
    if (toggleWBO) {
      const style = doc.createElement("style");
      style.id = STYLE_ID;
      style.textContent = "#new-toolbox, .new-toolbox { display: none !important; }";
      doc.head.appendChild(style);
    }
  }, [toggleWBO]);

  // 監聽白板 iframe 傳送之馬賽克幾何區域更新
  useEffect(() => {
    const handleWboMessage = (event) => {
      if (event.source === whiteboardFrameRef.current?.contentWindow &&
          event.origin === new URL(WHITEBOARD_BASE_URL, window.location.href).origin &&
          event?.data?.type === "WBO_MOSAICS_UPDATE" && Array.isArray(event.data.mosaics)) {
        setMosaics(event.data.mosaics.filter(m => m && typeof m.id === "string" &&
          [m.left, m.top, m.width, m.height].every(Number.isFinite) && m.width > 0 && m.height > 0));
      }
    };
    window.addEventListener("message", handleWboMessage);
    return () => window.removeEventListener("message", handleWboMessage);
  }, []);

  // ── 取得執行時設定（Jitsi 位址）────────────────────────────────
  useEffect(() => {
    if (BUILD_TIME_DOMAIN) return;   // .env 有指定就不必問後端
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/config`)
      .then((res) => res.json())
      .then((cfg) => {
        if (!cancelled) setJitsiDomain(resolveJitsiDomain(cfg));
      })
      .catch(() => {
        // 後端拿不到就退回預設埠，至少讓同機部署的情況能動
        if (!cancelled) setJitsiDomain(resolveJitsiDomain(null));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── 取得 JWT（僅在後端有開 token 驗證時）──────────────────────
  useEffect(() => {
    if (!JWT_ENABLED) {
      setTokenReady(true);
      return;
    }

    const url =
      `${API_BASE_URL}/api/token?room=${encodeURIComponent(roomName)}` +
      `&role=${encodeURIComponent(role)}` +
      `&displayName=${encodeURIComponent(displayName)}` +
      `&company=${encodeURIComponent(companyId)}`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          alert(`❌ ${t("meeting.tokenFailed", { error: data.error })}`);
          navigate("/");
          return;
        }
        setJwtToken(data.jwt);
        setTokenReady(true);
      })
      .catch((err) => {
        console.error("獲取 JWT 失敗", err);
        alert(t("meeting.tokenServerDown"));
        navigate("/");
      });
    // 不把 t 放進依賴：切換語言不該重新向後端要一次會議憑證
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomName, role, companyId, displayName, navigate]);

  // ── Jitsi 設定 ────────────────────────────────────────────────
  const appConfig = {
    // Jitsi 自己的介面（工具列、設定、提示訊息）跟著我們的語言走
    defaultLanguage: jitsiLang(lang),
    disableDominantSpeakerIndicator: true,
    disableTileView: true,
    startWithAudioMuted: !isHost,
    // 只有主持人要出畫面；非主持人預設不開攝影機（工具列也沒有攝影機按鈕，只能開關麥克風）
    startWithVideoMuted: !isHost,
    disableModeratorIndicator: false,
    startAudioOnly: false,
    doNotFlipLocalVideo: true,
    channelLastN: 1,

    // 內網部署：不要對外抓 gravatar / analytics / 公用 STUN，否則載入會卡住。
    disableThirdPartyRequests: true,
    analytics: { disabled: true },
    // 兩人會議走 P2P：兩端直接互傳，不經過 JVB。
    // Windows 上 JVB 跑在 Docker（WSL2 NAT），UDP 媒體要繞一層使用者空間的
    // 埠轉發代理，延遲、掉包都出在這裡；兩人時改走 P2P 就整個繞開它。
    // 同一個區網靠 host 候選位址就連得上，不需要 STUN，所以清空 stunServers
    // （預設會去連外網的 jitsi.net，內網只會逾時）。
    // P2P 協商失敗時 Jitsi 會留在 JVB 上，不會斷線；第三人進來也會自動切回 JVB。
    p2p: {
      enabled: true,
      stunServers: [],
      codecPreferenceOrder: DESKTOP_CODEC_ORDER,
    },
    videoQuality: {
      codecPreferenceOrder: DESKTOP_CODEC_ORDER,
    },
    prejoinConfig: { enabled: false },

    // 手機瀏覽器開會議時，Jitsi 預設會先跳一頁「您想如何加入此會議？」，
    // 上面是 Jitsi 自己的 logo 和「Jitsi Meet」字樣，還推你去 App Store。
    // 內網部署沒有意義（那個 App 連不到我們的伺服器），而且蓋掉我們的品牌。
    // 伺服器端的 DISABLE_DEEP_LINKING 也設了，這裡是雙保險。
    disableDeepLinking: true,

    // 新版 Jitsi 的工具列設定已從 interfaceConfig 搬到 config。
    // 非主持人只需要開關麥克風：拿掉攝影機與參與者名單，留設定（換裝置）與掛斷。
    toolbarButtons: isHost
      ? ["microphone", "camera", "settings", "participants-pane", "hangup"]
      : ["microphone", "settings", "hangup"],

    // 非主持人不能拉開右側縮圖列：畫面固定跟著主持人（followMe 釘選），
    // 也就不會有人自己點別人的縮圖把畫面切走。
    filmstrip: { disabled: !isHost },

    // 手機（觸控）不顯示頂部的會議資訊列（房名、計時、連線品質），
    // 螢幕已經很小，那一排會擋住畫面和白板的「⋯」。
    ...(isTouchDevice && { conferenceInfo: { alwaysVisible: [], autoHide: [] } }),
  };

  // 會議畫面裡的品牌。因為 Jitsi 現在掛在同一個來源，
  // /brand/logo.png 這種相對路徑在它的 iframe 裡也解析得到。
  const showOurLogo = Boolean(brand.logo) && brand.showJitsiWatermark !== false;

  const interfaceConfig = {
    // 舊版 Jitsi 相容用；新版讀上面的 toolbarButtons。
    TOOLBAR_BUTTONS: ["microphone", "camera", "overflowmenu", "hangup"],
    // 新版 Jitsi 的語言選單放在設定的「更多」分頁：要有 "more" 分頁才會出現，
    // "language" 決定分頁裡要不要顯示語言下拉選單，兩個缺一不可。
    SETTINGS_SECTIONS: ["devices", "language", "more", "moderator"],
    APP_NAME: brand.appName,
    PROVIDER_NAME: brand.appName,
    // 用我們自己的 LOGO 取代 Jitsi 的浮水印
    SHOW_JITSI_WATERMARK: showOurLogo,
    JITSI_WATERMARK_LINK: "",
    DEFAULT_LOGO_URL: brand.logo || undefined,
    DEFAULT_WELCOME_PAGE_LOGO_URL: brand.logo || undefined,
    SHOW_WATERMARK_FOR_GUESTS: showOurLogo,
    SHOW_BRAND_WATERMARK: false,
    SHOW_POWERED_BY: false,
    DISABLE_FOCUS_INDICATOR: true,
    DISABLE_DOMINANT_SPEAKER_INDICATOR: true,
    DISABLE_TRANSCRIPTION_SUBTITLES: true,
    DISABLE_VIDEO_BACKGROUND: true,
    FILM_STRIP_MAX_HEIGHT: 100,
    TILE_VIEW_MAX_COLUMNS: 1,
    DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
    DISABLE_PRESENCE_STATUS: true,
    DISABLE_RINGING: true,
  };

  // ── 主持人固定選用特定攝影機 (如 "ORMASTER MULTI VIEW") ─────────
  const targetCameraName = params.get("camera") || "ORMASTER MULTI VIEW";

  const selectPreferredCamera = async (api) => {
    if (!api || typeof api.getAvailableDevices !== "function") return;
    try {
      const devices = await api.getAvailableDevices();
      const videoDevices = devices?.videoInput || [];

      // 優先尋找名稱包含目標關鍵字的攝影機（不分大小寫）
      const preferred = videoDevices.find((device) =>
        (device.label || "").toUpperCase().includes(targetCameraName.toUpperCase())
      );

      if (preferred) {
        console.log(`[MeetingRoom] 主持人自動選用攝影機: ${preferred.label} (${preferred.deviceId})`);
        if (typeof api.setVideoInputDevice === "function") {
          api.setVideoInputDevice(preferred.label, preferred.deviceId);
        } else {
          api.executeCommand("setVideoInputDevice", preferred.label, preferred.deviceId);
        }
      }
    } catch (err) {
      console.warn("[MeetingRoom] 自動切換攝影機裝置失敗:", err);
    }
  };

  // ── 穩定心跳回報：只要進入會議頁面即開始發送，不依賴 Jitsi 內部私有事件 ──────
  useEffect(() => {
    if (!roomName) return;

    const sendHeartbeat = () => {
      const count = (apiRef.current?.getParticipantsInfo?.()?.length || 0) + 1;
      fetch(`${API_BASE_URL}/api/rooms/${encodeURIComponent(roomName)}/heartbeat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ count }),
      }).catch(() => {});
    };

    const sendLeave = () => {
      try {
        navigator.sendBeacon?.(`${API_BASE_URL}/api/rooms/${encodeURIComponent(roomName)}/leave`);
      } catch (e) {}
    };

    sendHeartbeat();
    const heartbeatTimer = setInterval(sendHeartbeat, 10000);
    window.addEventListener("beforeunload", sendLeave);

    return () => {
      clearInterval(heartbeatTimer);
      window.removeEventListener("beforeunload", sendLeave);
      sendLeave();
    };
  }, [roomName]);

  const handleApiReady = (api) => {
    apiRef.current = api;

    // 右側縮圖列預設收起，讓主畫面滿版；要看再按「>」展開。
    // Jitsi 只有 toggleFilmStrip（切換），沒有「設成關閉」，所以靠
    // filmstripDisplayChanged 記住目前狀態：已經是收起的（例如 followMe 先同步過來）
    // 就不要再切，否則反而會打開。
    let filmstripVisible = true;
    api.addEventListener("filmstripDisplayChanged", ({ visible }) => {
      filmstripVisible = visible;
    });

    api.addEventListener("videoConferenceLeft", () => {
      try {
        navigator.sendBeacon?.(`${API_BASE_URL}/api/rooms/${encodeURIComponent(roomName)}/leave`);
      } catch (e) {}
      navigate("/");
    });

    api.addEventListener("videoConferenceJoined", (event) => {
      myIdRef.current = event.id;
      joinedAtRef.current = Date.now();
      api.executeCommand("setTileView", false);
      // 非主持人的縮圖列整個停用（filmstrip.disabled），不需要也不該再切換
      if (isHost && filmstripVisible) api.executeCommand("toggleFilmStrip");

      // 在 Jitsi 的 iframe 裡加一段常駐樣式（同源才做得到）：
      // - touch-action：Jitsi 的工具列平常是隱藏的，手機上第一下只會叫出工具列，
      //   使用者自然會再點一下，在 iPhone 上這就是「點兩下放大」。手勢從 Jitsi
      //   iframe 裡開始時看的是 iframe 裡的 touch-action，所以要在它裡面也關掉。
      // - toolbox-content-wrapper::after：Jitsi 在 iPhone 上會在工具列下面補一塊
      //   同色區域讓開底部 home bar（safe-area-inset-bottom）。我們的 Jitsi 不貼螢幕
      //   底部，這塊就成了工具列下面多出來的一條；電腦上它本來就是 0 高度。
      // - stage-participant-label：大畫面上的名字標籤（例如「主持人」），不顯示。
      try {
        const doc = meetingFrameRef.current?.querySelector("iframe")?.contentDocument;
        if (doc?.head && !doc.getElementById("ormaster-jitsi-tweaks")) {
          const style = doc.createElement("style");
          style.id = "ormaster-jitsi-tweaks";
          style.textContent =
            "html, body { touch-action: manipulation; }" +
            ".toolbox-content-wrapper::after { display: none !important; }" +
            ".stage-participant-label { display: none !important; }";
          doc.head.appendChild(style);
        }
      } catch (e) {}

      if (isHost) {
        // 主持人釘選自己並開 followMe，其他人的畫面會自動跟著釘到主持人，
        // 不需要再輪詢比對 displayName（舊版那個比對永遠不成立）。
        api.executeCommand("pinParticipant", event.id);
        api.executeCommand("setFollowMe", true);

        // 主持人自動選用固定攝影機 ("ORMASTER MULTI VIEW")
        selectPreferredCamera(api);
        setTimeout(() => selectPreferredCamera(api), 800);
      }
    });

    // 裝置清單更新時，若為主持人亦自動切換至固定攝影機
    api.addEventListener("deviceListChanged", () => {
      if (isHost) {
        selectPreferredCamera(api);
      }
    });

    api.addEventListener("endpointTextMessageReceived", (res) => {
      const { type, action } = res?.data?.eventData?.text || {};
      if (type === "whiteboardControl") {
        setToggleWBO(action === "start");
      }
    });
  };

  // ── 參與者清單輪詢 ────────────────────────────────────────────
  const updateParticipants = useCallback(() => {
    if (!apiRef.current) return;
    const info = apiRef.current.getParticipantsInfo() || [];
    // 用 ID 排除自己。原本比對名字裡有沒有 "me"：中文介面自己標的是「（我）」比不到，
    // 名字剛好含 me 的人（例如 James）反而會被濾掉。
    const filtered = info.filter((p) => p.participantId !== myIdRef.current);
    if (JSON.stringify(filtered) !== JSON.stringify(participantsRef.current)) {
      participantsRef.current = filtered;
      setParticipants(filtered);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(updateParticipants, 5000);
    return () => clearInterval(interval);
  }, [updateParticipants]);

  // ── 主持人控制 ────────────────────────────────────────────────
  const muteEveryone = () => apiRef.current?.executeCommand("muteEveryone");

  const participantName = (p) =>
    p.displayName || p.formattedDisplayName || t("meeting.guest");

  const toggleKickList = () => {
    if (!kickOpen) updateParticipants(); // 名單平常 5 秒才更新一次，展開時先抓最新的
    setKickOpen(!kickOpen);
  };

  const kickParticipant = (p) => {
    if (!apiRef.current) return;
    if (!window.confirm(t("meeting.kickConfirm", { name: participantName(p) }))) return;
    apiRef.current.executeCommand("kickParticipant", p.participantId);
    setParticipants((prev) => prev.filter((x) => x.participantId !== p.participantId));
  };

  // ── 白板 ──────────────────────────────────────────────────────
  const toggleWhiteboard = () => {
    const next = !toggleWBO;
    apiRef.current?.executeCommand("sendEndpointTextMessage", "", {
      type: "whiteboardControl",
      action: next ? "start" : "stop",
    });
    setToggleWBO(next);
  };

  if (!tokenReady || !jitsiDomain) {
    return <div style={{ color: "white", padding: "2rem" }}>{t("meeting.loading")}</div>;
  }

  return (
    <div className="container-iframe">
      {/* 只在「直向 + 觸控」時由 CSS 顯示，轉成橫向會自動消失 */}
      {!rotateDismissed && (
        <div className="rotate-hint">
          <div className="rotate-icon">📱</div>
          <div>
            <span dangerouslySetInnerHTML={{ __html: t("rotate.title") }} />
            <br />
            <span style={{ fontSize: 14, opacity: 0.75 }}>{t("rotate.hint")}</span>
          </div>
          <button onClick={() => setRotateDismissed(true)}>{t("rotate.continue")}</button>
        </div>
      )}
      {/* 主持人控制：右下角兩顆圖示，文字放在 title／aria-label（跟著語言走）。
          移除參與者的名單平常收起，點圖示才在左側展開。 */}
      {isHost && (
        <div className="host-controls">
          <button
            className="icon-btn"
            onClick={muteEveryone}
            title={t("meeting.muteAll")}
            aria-label={t("meeting.muteAll")}
          >
            <MuteAllIcon />
          </button>

          <div className="kick-wrap" ref={kickRef}>
            <button
              className={`icon-btn${kickOpen ? " is-active" : ""}`}
              onClick={toggleKickList}
              aria-expanded={kickOpen}
              title={t("meeting.removeParticipant")}
              aria-label={t("meeting.removeParticipant")}
            >
              <RemoveUserIcon />
            </button>

            {kickOpen && (
              <div className="kick-panel" role="group" aria-label={t("meeting.removeParticipant")}>
                {participants.length === 0 ? (
                  <div className="kick-empty">{t("meeting.noParticipants")}</div>
                ) : (
                  participants.map((p) => (
                    <div className="kick-row" key={p.participantId}>
                      <span className="kick-name">{participantName(p)}</span>
                      <button
                        className="kick-remove"
                        onClick={() => kickParticipant(p)}
                        title={t("meeting.kick", { name: participantName(p) })}
                        aria-label={t("meeting.kick", { name: participantName(p) })}
                      >
                        <CloseIcon />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <button
        className={`whiteboard-btn${toggleWBO ? " is-active" : ""}`}
        aria-pressed={toggleWBO}
        onClick={toggleWhiteboard}
        title={t("meeting.whiteboard")}
        aria-label={t("meeting.whiteboard")}
      >
        <PencilIcon />
      </button>

      <JitsiMeeting
        domain={jitsiDomain}
        roomName={roomName}
        jwt={JWT_ENABLED ? jwtToken : undefined}
        configOverwrite={appConfig}
        userInfo={{ displayName }}
        interfaceConfigOverwrite={interfaceConfig}
        lang={jitsiLang(lang)}
        getIFrameRef={node => { meetingFrameRef.current = node; }}
        onApiReady={handleApiReady}
      />

      {toggleWBO && mosaics.length > 0 && (
        <VideoMosaic mosaics={mosaics} meetingFrameRef={meetingFrameRef} />
      )}

      {toggleWBO && (
        <div className="whiteboard-container" style={{ zIndex: 2 }}>
          <iframe
            ref={whiteboardFrameRef}
            onLoad={event => event.currentTarget.contentWindow?.postMessage(
              { type: "WBO_REQUEST_MOSAICS" },
              new URL(WHITEBOARD_BASE_URL, window.location.href).origin
            )}
            src={`${WHITEBOARD_BASE_URL}/${roomName}`}
            title="Whiteboard"
            className="whiteboard-iframe"
          ></iframe>
        </div>
      )}
    </div>
  );
};

export default MeetingRoom;
